/**
 * Generic tree utilities for transforming flat lists into hierarchical structures.
 * Used by Pflegeorte/Bereiche tree view and reusable for other tree-based UIs.
 */

export type TreeNode<T> = {
  id: string;
  parentId: string | null;
  children: TreeNode<T>[];
  data: T;
  depth: number;
};

type WithIdAndParent = {
  id: string;
  parentId: string | null;
};

/**
 * Transforms a flat array of items with id/parentId into a nested tree structure.
 * Items with parentId === null become root nodes.
 *
 * @param items - Flat array of items with id and parentId
 * @param sortFn - Optional sort function for ordering children
 * @returns Array of root-level TreeNodes with nested children
 */
export function buildTree<T extends WithIdAndParent>(
  items: T[],
  sortFn?: (a: T, b: T) => number
): TreeNode<T>[] {
  const itemMap = new Map<string, TreeNode<T>>();
  const roots: TreeNode<T>[] = [];

  // First pass: create all nodes
  items.forEach((item) => {
    itemMap.set(item.id, {
      id: item.id,
      parentId: item.parentId,
      children: [],
      data: item,
      depth: 0,
    });
  });

  // Second pass: build hierarchy
  items.forEach((item) => {
    const node = itemMap.get(item.id)!;
    if (item.parentId === null) {
      roots.push(node);
    } else {
      const parent = itemMap.get(item.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphan node (parent not found) - treat as root
        roots.push(node);
      }
    }
  });

  // Third pass: calculate depths and sort children
  function processNodes(nodes: TreeNode<T>[], depth: number) {
    if (sortFn) {
      nodes.sort((a, b) => sortFn(a.data, b.data));
    }
    nodes.forEach((node) => {
      node.depth = depth;
      processNodes(node.children, depth + 1);
    });
  }
  processNodes(roots, 0);

  return roots;
}

/**
 * Flattens a tree back to a flat array (depth-first order).
 * Useful for rendering in a FlatList while preserving visual hierarchy.
 */
export function flattenTree<T>(nodes: TreeNode<T>[]): TreeNode<T>[] {
  const result: TreeNode<T>[] = [];

  function traverse(nodeList: TreeNode<T>[]) {
    nodeList.forEach((node) => {
      result.push(node);
      traverse(node.children);
    });
  }

  traverse(nodes);
  return result;
}

/**
 * Finds a node in the tree by ID.
 */
export function findNodeById<T>(
  nodes: TreeNode<T>[],
  id: string
): TreeNode<T> | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}

/**
 * Counts total nodes in a tree (including all descendants).
 */
export function countNodes<T>(nodes: TreeNode<T>[]): number {
  let count = 0;
  function traverse(nodeList: TreeNode<T>[]) {
    nodeList.forEach((node) => {
      count++;
      traverse(node.children);
    });
  }
  traverse(nodes);
  return count;
}

/**
 * Gets all ancestor IDs for a given node ID.
 * Useful for auto-expanding parent nodes.
 */
export function getAncestorIds<T extends WithIdAndParent>(
  items: T[],
  nodeId: string
): string[] {
  const itemMap = new Map<string, T>();
  items.forEach((item) => itemMap.set(item.id, item));

  const ancestors: string[] = [];
  let current = itemMap.get(nodeId);

  while (current?.parentId) {
    ancestors.push(current.parentId);
    current = itemMap.get(current.parentId);
  }

  return ancestors;
}
